import React from 'react'
import { observer } from 'mobx-react-lite'

import { endOfMonth } from 'date-fns/endOfMonth'
import { formatISO } from 'date-fns/formatISO'
import { fromUnixTime } from 'date-fns/fromUnixTime'
import { startOfMonth } from 'date-fns/startOfMonth'
import { startOfToday } from 'date-fns/startOfToday'
import { subDays } from 'date-fns/subDays'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'

import { H2, P } from '@mxenabled/mxui'
import { Add } from '@mxenabled/mx-icons'

import AccountsListCompact from '../../../accounts/components/AccountsListCompact'
import {
  ConnectDrawer,
  Drawer,
  useAccountStore,
  useFinstrongStore,
  useGlobalCopyStore,
  useRecurringTransactionsStore,
  useTransactionStore,
  useUserStore,
} from '../../../common'
import {
  Frequency,
  type RepeatingTransaction,
  RepeatingType,
  type Transaction,
} from '../../../core'
import { OnboardingSteps } from '../../../core/types/Finstrong'
import { SelectTransaction } from '../../../recurringtransactions'
import { getNextPaymentDate } from '../../../core/utils/RecurringTransactionsUtil'
import { incomeFilter, incomeFilterSearch } from '../../../transactions/utils/Filters'

import { CALCULATING_ANIMATION_URL } from '../../constants'

import OnboardingAddMonthlyIncome from './OnboardingAddMonthlyIncome'
import OnboardingBirthday from './OnboardingBirthday'
import OnboardingCreditScore from './OnboardingCreditScore'
import OnboardingStepsHeader from './OnboardingStepsHeader'

interface IncomeTransaction extends Transaction {
  firstDay: number
  frequency: Frequency
  secondDay: number
}

interface OnboardingStepsDrawerProps {
  isOpen: boolean
  onClose: () => void
}

interface Steps {
  detail?: React.ReactNode
  detailActionContent?: string
  main: React.ReactNode
  mainActionContent: string
  primaryText: string
  secondaryText: string
}

type StepType = 'main' | 'detail'
export interface StepState {
  step: OnboardingSteps
  type: StepType
}

const INITIAL_STEP_STATE: StepState = {
  step: OnboardingSteps.ConnectAccounts,
  type: 'main',
}

const OnboardingStepsDrawer = ({ isOpen, onClose }: OnboardingStepsDrawerProps) => {
  const { visibleAccounts: accounts } = useAccountStore()
  const { calculateHealthScore } = useFinstrongStore()
  const { finstrong: copy } = useGlobalCopyStore()
  const { addRepeatingTransaction, setDateRange } = useRecurringTransactionsStore()
  const { filter, setFilter, transactions } = useTransactionStore()
  const { user, userProfile, updateUser, updateUserProfile } = useUserStore()

  const [creditScore, setCreditScore] = React.useState<number>(user.credit_score || 0)
  const [currentStep, setCurrentStep] = React.useState<{ step: OnboardingSteps; type: StepType }>(
    INITIAL_STEP_STATE,
  )
  const [firstDay, setFirstDay] = React.useState(1)
  const [frequency, setFrequency] = React.useState(Frequency.EveryMonth)
  const [incomeTransactions, setIncomeTransactions] = React.useState<IncomeTransaction[]>([])
  const [isComplete, setIsComplete] = React.useState(false)
  const [isConnectDrawerOpen, setIsConnectDrawerOpen] = React.useState(false)
  const [selectedDate, setSelectedDate] = React.useState<number | null | undefined>(user.birthday)
  const [secondDay, setSecondDay] = React.useState(15)
  const [transactionGuid, setTransactionGuid] = React.useState('')

  React.useEffect(() => {
    setDateRange({ start: startOfMonth(startOfToday()), end: endOfMonth(startOfToday()) })
  }, [])

  React.useEffect(() => {
    setFilter({
      accounts: accounts.map((account) => account.guid),
      dateRange: {
        start: subDays(new Date(), 90),
        end: new Date(),
      },
      custom: incomeFilter,
    })
  }, [accounts])

  const transaction = React.useMemo(
    () => transactions.find((t) => t.guid === transactionGuid),
    [transactionGuid, transactions],
  )

  const isPrimaryActionEnabled = React.useMemo(() => {
    const isValid: Partial<Record<OnboardingSteps, boolean>> = {
      [OnboardingSteps.ConnectAccounts]: true,
      [OnboardingSteps.MonthlyIncome]: !!transactionGuid,
      [OnboardingSteps.Birthday]: !!user.birthday || !!selectedDate,
      [OnboardingSteps.CreditScore]: !!user.credit_score || !!creditScore,
    }

    return isValid[currentStep.step]
  }, [creditScore, currentStep, selectedDate, transactionGuid, user])

  const hasAccounts = accounts.length > 0
  const progressValue = React.useMemo(() => {
    const { step } = currentStep

    let value = 0

    if (step >= OnboardingSteps.MonthlyIncome && hasAccounts) value++
    if (step >= OnboardingSteps.Birthday && !!incomeTransactions.length) value++
    if (step >= OnboardingSteps.CreditScore && (user.birthday || user.credit_score)) value++

    return value
  }, [currentStep.step, hasAccounts, incomeTransactions.length, user.birthday, user.credit_score])

  const handleSelectTransaction = (searchValue: string) => {
    setFilter({
      ...filter,
      custom: (transaction: Transaction) => {
        if (searchValue) return incomeFilterSearch(transaction, searchValue)

        return incomeFilter(transaction)
      },
    })
  }

  const steps: Partial<Record<OnboardingSteps, Steps>> = {
    [OnboardingSteps.ConnectAccounts]: {
      main: hasAccounts ? <AccountsListCompact shouldShowBalance={false} /> : null,
      mainActionContent: hasAccounts ? copy.onboarding_next : copy.onboarding_add_accounts,
      primaryText: copy.connect_your_accounts,
      secondaryText: copy.onboarding_connect_your_accounts_secondary,
    },
    [OnboardingSteps.MonthlyIncome]: {
      detail: (
        <OnboardingAddMonthlyIncome
          firstDay={firstDay}
          frequency={frequency}
          incomeTransactions={incomeTransactions}
          secondDay={secondDay}
          setCurrentStep={(step) => {
            setCurrentStep(step)
            setIncomeTransactions((prev) => {
              const exists = prev.some((t) => t.guid === transactionGuid)
              if (exists) return prev.filter((t) => t.guid !== transactionGuid)

              const newTransaction = transactions.find((t) => t.guid === transactionGuid)
              return newTransaction
                ? [...prev, { ...newTransaction, firstDay, frequency, secondDay }]
                : prev
            })
          }}
          setFirstDay={setFirstDay}
          setFrequency={setFrequency}
          setSecondDay={setSecondDay}
          transaction={transaction!}
        />
      ),
      detailActionContent: copy.onboarding_confirm_income,
      main: (
        <SelectTransaction
          onSearch={handleSelectTransaction}
          onSelected={(guid) => {
            setCurrentStep({ step: OnboardingSteps.MonthlyIncome, type: 'detail' })
            setTransactionGuid(guid)
          }}
        />
      ),
      mainActionContent: copy.onboarding_confirm_income,
      primaryText: copy.onboarding_add_income_primary,
      secondaryText: copy.onboarding_add_income_secondary,
    },
    [OnboardingSteps.Birthday]: {
      main: <OnboardingBirthday selectedDate={selectedDate} setSelectedDate={setSelectedDate} />,
      mainActionContent: copy.onboarding_next,
      primaryText: copy.onboarding_birthday_primary,
      secondaryText: copy.onboarding_birthday_secondary,
    },
    [OnboardingSteps.CreditScore]: {
      main: <OnboardingCreditScore creditScore={creditScore} setCreditScore={setCreditScore} />,
      mainActionContent: copy.onboarding_next,
      primaryText: copy.onboarding_credit_score_primary,
      secondaryText: copy.onboarding_credit_score_secondary,
    },
  }

  const handleClose = () => {
    setCurrentStep(INITIAL_STEP_STATE)
    setIsComplete(false)
    setTransactionGuid('')

    onClose()
  }

  const handleComplete = async () => {
    setIsComplete(true)

    await addRecurringIncome(incomeTransactions)
    await calculateHealthScore()
    await updateUserProfile({ ...userProfile, has_completed_finstrong_onboarding: true })

    handleClose()
  }

  const handleDoItLater = () => {
    if (currentStep.step === OnboardingSteps.CreditScore) {
      handleComplete()
      return
    }

    setCurrentStep({ step: currentStep.step + 1, type: 'main' })
  }

  const handlePrimaryAction = () => {
    const { step } = currentStep
    const nextStep = step + 1

    switch (step) {
      case OnboardingSteps.Birthday:
        if (selectedDate && selectedDate !== user.birthday) {
          updateUser({ ...user, birthday: selectedDate })
        }
        break

      case OnboardingSteps.CreditScore:
        if (creditScore && creditScore !== user.credit_score) {
          updateUser({ ...user, credit_score: creditScore })
        }
        handleComplete()
        return

      case OnboardingSteps.MonthlyIncome:
        if (
          transactionGuid &&
          incomeTransactions.findIndex(({ guid }) => guid === transactionGuid) === -1
        ) {
          setIncomeTransactions((prev) => [
            ...prev,
            { ...transaction!, firstDay, frequency, secondDay },
          ])
        }
        break

      case OnboardingSteps.ConnectAccounts:
        if (!hasAccounts) {
          setIsConnectDrawerOpen(true)
          return
        }
        break

      default:
        break
    }

    if (nextStep in OnboardingSteps) setCurrentStep({ step: nextStep, type: 'main' })
  }

  const handleSecondaryAction = () => {
    const { step, type } = currentStep

    if (type === 'detail') {
      setCurrentStep({ step, type: 'main' })
      return
    }

    if (step === OnboardingSteps.Birthday && incomeTransactions.length) {
      setCurrentStep({ step: step - 1, type: 'detail' })
      return
    }

    if (step > OnboardingSteps.ConnectAccounts) setCurrentStep({ step: step - 1, type: 'main' })
  }

  const addRecurringIncome = async (transactions: IncomeTransaction[]) => {
    for (const t of transactions) {
      const {
        account_guid,
        amount,
        date,
        description,
        feed_description,
        firstDay,
        frequency,
        secondDay,
        merchant_guid,
        payee,
        transaction_type,
      } = t

      const nextDate = getNextPaymentDate(frequency, fromUnixTime(date))

      const repeatingTransaction = {
        account_guid,
        amount,
        created_by: 0,
        description: payee || description || feed_description,
        merchant_guid,
        predicted_occurs_on: formatISO(nextDate, { representation: 'date' }),
        recurrence_day: firstDay,
        recurrence_type: frequency,
        repeating_transaction_type: RepeatingType.Income,
        second_recurrence_day: secondDay,
        transaction_type,
        user_guid: user.guid,
      } as RepeatingTransaction

      await addRepeatingTransaction(repeatingTransaction, t)
    }
  }

  const currentStepData = steps[currentStep.step]
  const isConnectAccountsStep = currentStep.step === OnboardingSteps.ConnectAccounts
  const primaryButtonProps = {
    fullWidth: true,
    ...(isConnectAccountsStep && !hasAccounts && { startIcon: <Add /> }),
  }
  const showDoItLaterButton = !isConnectAccountsStep || (isConnectAccountsStep && !hasAccounts)

  return (
    <React.Fragment>
      <Drawer
        footerStyles={{ borderTop: 1, borderColor: 'divider', px: 24, py: 16 }}
        hasSecondaryAction={!isConnectAccountsStep}
        isOpen={isOpen}
        isPrimaryDisabled={!isPrimaryActionEnabled}
        onClose={handleClose}
        onSecondaryAction={handleSecondaryAction}
        primaryButtonProps={primaryButtonProps}
        primaryText={currentStepData?.mainActionContent}
        secondaryColor="primary"
        secondaryText={copy.onboarding_back}
        title={copy.title}
        {...(!isComplete && { onPrimaryAction: handlePrimaryAction })}
      >
        <Stack sx={{ bgcolor: 'background.default', height: '100%' }}>
          {isComplete && (
            <Stack sx={{ alignItems: 'center', gap: 24, position: 'relative', px: 16, top: 126 }}>
              <img alt="" src={CALCULATING_ANIMATION_URL} style={{ minHeight: 100, width: 211 }} />
              <H2>{copy.onboarding_calculate_primary}</H2>
              <P sx={{ textAlign: 'center' }} variant="subtitle1">
                {copy.onboarding_calculate_secondary}
              </P>
            </Stack>
          )}
          {currentStepData && !isComplete && (
            <React.Fragment>
              <OnboardingStepsHeader
                completeCopy={copy.complete}
                primaryText={currentStepData.primaryText}
                secondaryText={currentStepData.secondaryText}
                value={progressValue}
              />
              {currentStep.type === 'main' && (
                <Box sx={{ bgcolor: 'background.default' }}>
                  {currentStepData.main}
                  {showDoItLaterButton && (
                    <Stack sx={{ alignItems: 'center', px: 24, py: 12 }}>
                      <Button fullWidth={true} onClick={handleDoItLater}>
                        {copy.onboarding_do_it_later}
                      </Button>
                    </Stack>
                  )}
                </Box>
              )}
              {currentStep.type === 'detail' && currentStepData.detail}
            </React.Fragment>
          )}
        </Stack>
      </Drawer>

      <ConnectDrawer
        onClose={() => setIsConnectDrawerOpen(false)}
        showConnectWidget={isConnectDrawerOpen}
        title={copy.connect_more_accounts_button}
      />
    </React.Fragment>
  )
}

export default observer(OnboardingStepsDrawer)
